import React from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, X } from "lucide-react";
import { money } from "@/components/shared";

export default function LineItemsEditor({ label, items, setItems, testid, skuOptions, showUnit = true }) {
  const add = () => setItems([...items, { name: "", sku: "", qty: 1, unit: "nos", rate: 0 }]);
  const update = (i, field, val) => {
    const copy = [...items];
    copy[i][field] = val;
    setItems(copy);
  };
  const remove = (i) => setItems(items.filter((_, idx) => idx !== i));
  const total = items.reduce((s, it) => s + (Number(it.qty) || 0) * (Number(it.rate) || 0), 0);

  const hasSku = Array.isArray(skuOptions);
  const nameSpan = hasSku ? "col-span-3" : showUnit ? "col-span-5" : "col-span-6";
  const rateSpan = !hasSku && !showUnit ? "col-span-3" : "col-span-2";

  return (
    <div>
      <div className="mb-2 flex items-center justify-between">
        <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</p>
        <Button type="button" variant="outline" size="sm" data-testid={`${testid}-add`} onClick={add} className="h-7 gap-1 text-xs">
          <Plus className="h-3 w-3" /> Add row
        </Button>
      </div>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="grid grid-cols-12 gap-2">
            <Input className={nameSpan} placeholder="Item" data-testid={`${testid}-name-${i}`}
              value={it.name} onChange={(e) => update(i, "name", e.target.value)} />
            {hasSku && (
              <div className="col-span-3">
                <Select value={it.sku || ""} onValueChange={(v) => update(i, "sku", v)}>
                  <SelectTrigger data-testid={`${testid}-sku-${i}`}><SelectValue placeholder="SKU" /></SelectTrigger>
                  <SelectContent>
                    {skuOptions.length === 0 && <SelectItem value="none" disabled>Pick a buyer first</SelectItem>}
                    {skuOptions.map((o) => <SelectItem key={o.sku} value={o.sku}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <Input className="col-span-2" type="number" placeholder="Qty" data-testid={`${testid}-qty-${i}`}
              value={it.qty} onChange={(e) => update(i, "qty", e.target.value)} />
            {showUnit && (
              <Input className={hasSku ? "col-span-1" : "col-span-2"} placeholder="Unit"
                value={it.unit} onChange={(e) => update(i, "unit", e.target.value)} />
            )}
            <Input className={rateSpan} type="number" placeholder="Rate" data-testid={`${testid}-rate-${i}`}
              value={it.rate} onChange={(e) => update(i, "rate", e.target.value)} />
            <Button type="button" variant="ghost" size="icon" onClick={() => remove(i)}
              className="col-span-1 h-9 w-9 text-muted-foreground hover:text-destructive"><X className="h-4 w-4" /></Button>
          </div>
        ))}
        {items.length === 0 && <p className="py-2 text-sm text-muted-foreground">No rows yet. Click "Add row".</p>}
      </div>
      <div className="mt-2 flex justify-end text-sm">
        <span className="text-muted-foreground">Total:&nbsp;</span>
        <span className="font-heading font-bold">{money(total)}</span>
      </div>
    </div>
  );
}
